'use client';

import React, { type ReactNode, type RefObject } from 'react';
import Link from 'next/link';
import {
  Bell,
  HelpCircle,
  MessageCircle,
  Menu,
  Moon,
  PanelRight,
  Search,
  Sun,
  UsersRound,
  X,
} from 'lucide-react';
import type { ShellTokens } from '@/lib/shell/tokens';
import ShellCreateMenu, { type ShellCreateItem } from '@/components/shell/ShellCreateMenu';

export type TopCommandBarProps = {
  variant: 'individual' | 'institution';
  tokens: ShellTokens;
  isOpal: boolean;
  /** Small uppercase workspace line above the page title. */
  eyebrow?: string;
  title: string;
  nav: {
    open: boolean;
    controlsId: string;
    onToggle: () => void;
    openLabel: string;
    closeLabel: string;
  };
  search?: {
    placeholder: string;
    onOpen: () => void;
    buttonRef: RefObject<HTMLButtonElement | null>;
  };
  create?: {
    label: string;
    items: ShellCreateItem[];
  };
  notifications?: {
    href: string;
    unreadCount: number | null;
    label: string;
    onClick?: () => void;
  };
  messages?: {
    href: string;
    unreadCount: number | null;
    label: string;
  };
  help?: { href: string; label: string };
  settings?: { href: string; label: string };
  onToggleTheme: () => void;
  themeLabels: { toDark: string; toLight: string };
  rail?: {
    visible: boolean;
    open: boolean;
    onToggle: () => void;
    label: string;
    controlsId: string;
    buttonRef: RefObject<HTMLButtonElement | null>;
  };
  /** Extra end-of-bar controls a surface wants to keep (language selector, messages badge). */
  endSlot?: ReactNode;
  /** A compact brand mark rendered before the title block (individual surface only). */
  brand?: ReactNode;
};

// Deliberately no `display` here: a couple of consumers combine this with a
// responsive-hide class (e.g. `md:hidden`), and an inline display would always
// win over that media-query rule regardless of viewport. Callers add `flex`
// to their own className instead.
function iconButtonStyle(tokens: ShellTokens, active = false): React.CSSProperties {
  return {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    border: `1px solid ${active ? tokens.activeBorder : tokens.border}`,
    background: active ? tokens.accentSoft : tokens.surface,
    color: active ? tokens.accent : tokens.textMuted,
    cursor: 'pointer',
    textDecoration: 'none',
    flexShrink: 0,
    position: 'relative',
  };
}

function UnreadBadge({ count, tokens }: { count: number; tokens: ShellTokens }) {
  return (
    <span
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: -4,
        right: -4,
        minWidth: 15,
        height: 15,
        padding: '0 4px',
        borderRadius: 8,
        background: tokens.accent,
        color: '#ffffff',
        fontSize: 9,
        fontWeight: 800,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

/**
 * The compact global top command bar shared by the authenticated shells.
 *
 * Left region invariants are pinned by regression tests and must not change:
 * a single state-driven mobile nav toggle (hamburger when closed, X when the
 * drawer is open, never an X on desktop because the control itself is lg:hidden)
 * followed by a truncating workspace/title block, with the gap carried by the
 * flex row rather than a margin.
 */
export default function TopCommandBar({
  variant,
  tokens,
  isOpal,
  eyebrow,
  title,
  nav,
  search,
  create,
  notifications,
  messages,
  help,
  settings,
  onToggleTheme,
  themeLabels,
  rail,
  endSlot,
  brand,
}: TopCommandBarProps) {
  return (
    <div
      data-testid="shell-top-command-bar"
      style={{
        height: 56,
        flexShrink: 0,
        background: tokens.topbar,
        borderBottom: `1px solid ${tokens.topbarBorder}`,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '0 16px 0 20px',
        position: 'relative',
        overflow: 'hidden',
        backdropFilter:
          variant === 'institution' && isOpal ? 'blur(18px) saturate(120%)' : undefined,
        WebkitBackdropFilter:
          variant === 'institution' && isOpal ? 'blur(18px) saturate(120%)' : undefined,
        boxShadow:
          variant === 'institution' && isOpal
            ? '0 1px 2px rgba(45,40,70,0.035), 0 8px 24px rgba(75,65,120,0.045)'
            : undefined,
      }}
    >
      {/* Left — mobile menu OR desktop label. Gap lives on the flex row (not a margin on the
          title block) so it collapses cleanly to zero when the button is display:none at lg+.
          `flex-basis: auto` (not the `1 1 0%` shorthand of a bare `flex: 1`): this region's
          own content (toggle + brand + title) must establish its real minimum size first: a
          0% basis instead starts this region at zero and hands nearly all the row's width to
          search/the icon group before this region gets any, which on a wider icon set (e.g.
          individual's) starves the title down to zero rather than merely truncating it. */}
      <div
        style={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', minWidth: 0, gap: 14 }}
      >
        <button
          type="button"
          onClick={nav.onToggle}
          // `flex` (not an inline display style) so the lg:hidden media-query
          // rule -- generated later in Tailwind's stylesheet, same specificity
          // -- can actually win at desktop widths. An inline style here would
          // always beat it regardless of viewport, since inline styles outrank
          // any external stylesheet rule short of !important.
          className="flex lg:hidden"
          style={{
            width: 34,
            height: 34,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            border: `1px solid ${tokens.border}`,
            background: tokens.surface,
            color: tokens.textMuted,
            cursor: 'pointer',
            flexShrink: 0,
          }}
          aria-expanded={nav.open}
          aria-controls={nav.controlsId}
          aria-label={nav.open ? nav.closeLabel : nav.openLabel}
        >
          {nav.open ? <X size={16} /> : <Menu size={16} />}
        </button>

        {brand}

        <div className="min-w-0" data-testid="shell-topbar-title-block">
          {eyebrow ? (
            <span
              className="block truncate text-[10px] font-bold uppercase tracking-[0.08em]"
              style={{ color: tokens.sectionLabel }}
            >
              {eyebrow}
            </span>
          ) : null}
          <span
            className="block truncate"
            data-testid="shell-topbar-title"
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: tokens.textPrimary,
            }}
          >
            {title}
          </span>
        </div>
      </div>

      {/* Middle — universal search stays the central global action on desktop. */}
      {search ? (
        <button
          ref={search.buttonRef}
          type="button"
          onClick={search.onOpen}
          aria-label={search.placeholder}
          className="shell-transition hidden md:flex"
          style={{
            alignItems: 'center',
            gap: 8,
            width: 'min(300px, 30vw)',
            height: 32,
            padding: '0 12px',
            borderRadius: 9,
            border: `1px solid ${tokens.border}`,
            background:
              variant === 'institution' && isOpal
                ? 'rgba(255,255,255,0.66)'
                : isOpal
                  ? 'rgba(0,0,0,0.03)'
                  : 'rgba(255,255,255,0.04)',
            color: tokens.textMuted,
            cursor: 'pointer',
            flexShrink: 1,
            minWidth: 0,
          }}
        >
          <Search size={14} aria-hidden="true" style={{ flexShrink: 0 }} />
          <span
            style={{
              fontSize: 12,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {search.placeholder}
          </span>
        </button>
      ) : null}

      {/* Right — global actions. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        {search ? (
          <button
            type="button"
            onClick={search.onOpen}
            aria-label={search.placeholder}
            title={search.placeholder}
            className="flex shell-transition shell-hit-target md:hidden"
            style={iconButtonStyle(tokens)}
          >
            <Search size={14} aria-hidden="true" />
          </button>
        ) : null}

        {create ? (
          <ShellCreateMenu tokens={tokens} label={create.label} items={create.items} />
        ) : null}

        {rail?.visible ? (
          <button
            ref={rail.buttonRef}
            type="button"
            onClick={rail.onToggle}
            aria-expanded={rail.open}
            aria-controls={rail.controlsId}
            aria-label={rail.label}
            title={rail.label}
            className="flex shell-transition shell-hit-target"
            style={iconButtonStyle(tokens, rail.open)}
          >
            <PanelRight size={14} aria-hidden="true" />
          </button>
        ) : null}

        <button
          type="button"
          onClick={onToggleTheme}
          aria-label={isOpal ? themeLabels.toDark : themeLabels.toLight}
          title={isOpal ? themeLabels.toDark : themeLabels.toLight}
          className="flex shell-transition shell-hit-target"
          style={iconButtonStyle(tokens)}
        >
          {isOpal ? <Moon size={14} aria-hidden="true" /> : <Sun size={14} aria-hidden="true" />}
        </button>

        {help ? (
          <Link
            href={help.href}
            aria-label={help.label}
            title={help.label}
            // Hidden below 640px so it doesn't compete with the title block for
            // space on very narrow phones (matches the individual dashboard's
            // own precedent of hiding Help below sm).
            className="hidden sm:flex shell-transition shell-hit-target"
            style={iconButtonStyle(tokens)}
          >
            <HelpCircle size={14} aria-hidden="true" />
          </Link>
        ) : null}

        {messages ? (
          <Link
            href={messages.href}
            aria-label={
              messages.unreadCount
                ? `${messages.label}, ${messages.unreadCount} unread`
                : messages.label
            }
            title={messages.label}
            // Hidden below 640px, same as Help: individual has one more always-on
            // icon than institution's proven-fits-at-320px set (rail, notifications,
            // settings, theme), so this is the one that yields first.
            className="hidden sm:flex shell-transition shell-hit-target"
            style={iconButtonStyle(tokens)}
          >
            <MessageCircle size={14} aria-hidden="true" />
            {messages.unreadCount != null && messages.unreadCount > 0 ? (
              <UnreadBadge count={messages.unreadCount} tokens={tokens} />
            ) : null}
          </Link>
        ) : null}

        {notifications ? (
          <Link
            href={notifications.href}
            onClick={notifications.onClick}
            aria-label={
              notifications.unreadCount
                ? `${notifications.label}, ${notifications.unreadCount} unread`
                : notifications.label
            }
            title={notifications.label}
            className="flex shell-transition shell-hit-target"
            style={iconButtonStyle(tokens)}
          >
            <Bell size={14} aria-hidden="true" />
            {notifications.unreadCount != null && notifications.unreadCount > 0 ? (
              <UnreadBadge count={notifications.unreadCount} tokens={tokens} />
            ) : null}
          </Link>
        ) : null}

        {settings ? (
          <Link
            href={settings.href}
            aria-label={settings.label}
            title={settings.label}
            className="flex shell-transition shell-hit-target"
            style={{
              ...iconButtonStyle(tokens),
              background: tokens.accentSoft,
              color: tokens.accent,
            }}
          >
            <UsersRound size={14} aria-hidden="true" />
          </Link>
        ) : null}

        {endSlot}
      </div>
    </div>
  );
}
